import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import BEMHelper from 'react-bem-helper';
import moment from 'moment';
import Text from '@splunk/react-ui/Text';

const className = new BEMHelper('default-token-expiration-configuration');

const VALID_DURATION_PATTERN = /^[0-9]+$/;
const DEFAULT_NUMBER = 30;

const currentDate = moment();
const futureDate = moment().add(18, 'years');

const daysIn18Years = futureDate.diff(currentDate, 'days');

function isValidDuration(duration, maxDays, invalidExceedsMessage) {
    const matches = VALID_DURATION_PATTERN.test(duration);

    if (matches) {
        const value = parseInt(duration, 10);

        if (value === 0) {
            return { isValid: false, message: 'Enter a value greater than 0.' };
        }

        return {
            isValid: value <= maxDays,
            message: value > maxDays ? invalidExceedsMessage : 'Valid input.',
        };
    }

    return { isValid: false, message: 'Invalid input.' };
}

export default function DefaultTokenExpirationConfiguration({
    label,
    onChange,
    value,
    additionalInfo,
    defaultNumber = DEFAULT_NUMBER,
    maxDays = daysIn18Years,
    invalidExceedsMessage = "Enter a day that's within the next 18 years.",
}) {
    const [number, setNumber] = useState(defaultNumber);

    useEffect(() => {
        const parsedValue = parseInt(value, 10);
        if (
            value &&
            number !== parsedValue &&
            isValidDuration(value, maxDays, invalidExceedsMessage).isValid
        ) {
            setNumber(value);
        }
    }, [invalidExceedsMessage, maxDays, number, value]);

    const handleChangeNumber = (_, { value }) => {
        onChange(value, isValidDuration(value, maxDays, invalidExceedsMessage).isValid);
        setNumber(value);
    };

    const { isValid, message } = isValidDuration(value, maxDays, invalidExceedsMessage);

    return (
        <div {...className()}>
            <label>
                <span {...className('input-label')}>{label}</span>
                <Text
                    {...className(
                        'duration-number',
                        isValidDuration(value, maxDays, invalidExceedsMessage).isValid
                            ? null
                            : 'invalid'
                    )}
                    type="number"
                    inline
                    name={label}
                    onChange={handleChangeNumber}
                    value={number}
                />
                days
            </label>
            {!isValid && <div {...className('error')}>{message}</div>}
            <div {...className('additional-information')}>{additionalInfo}</div>
        </div>
    );
}

DefaultTokenExpirationConfiguration.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    additionalInfo: PropTypes.string,
    defaultNumber: PropTypes.number,
    maxDays: PropTypes.number,
    invalidExceedsMessage: PropTypes.string,
};
